import React, { useEffect, useState } from 'react';
import {
  ShoppingCart, Euro, Package, TrendingUp, Clock, BarChart3
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { getMyDashboard, type BuyerDashboard } from '../../services/clientService';
import { handleApiError } from '../../utils/errorHandling';
// import { ShieldAlert } from 'lucide-react';

const statusLabels: Record<string, string> = {
  draft: 'Brouillon',
  pending: 'En attente',
  confirmed: 'Confirmée',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  cancelled: 'Annulée',
  invoiced: 'Facturée',
  paid: 'Payée',
  enregistrer: 'Enregistrée',
};

const STATUS_COLORS: Record<string, string> = {
  draft: '#94a3b8',      // slate-400
  pending: '#eab308',    // yellow-500
  confirmed: '#3b82f6',  // blue-500
  shipped: '#a855f7',    // purple-500
  delivered: '#22c55e',  // green-500
  invoiced: '#f59e0b',   // amber-500
  paid: '#10b981',       // emerald-500
  enregistrer: '#6366f1',  // indigo-500
  cancelled: '#6b7280',  // gray-500
};

const BuyerDashboardTailwind: React.FC = () => {
  const [dashboard, setDashboard] = useState<BuyerDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  const formatAmountHT = (amount?: number | null) => {
    return Math.round(amount || 0).toLocaleString('fr-FR');
  };

  useEffect(() => {
    void loadDashboard();
  }, []);

  const loadDashboard = async (): Promise<void> => {
    try {
      setLoading(true);
      const dashboardData = await getMyDashboard();
      setDashboard(dashboardData);
    } catch (error: any) {
      handleApiError(error, 'Erreur chargement dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !dashboard) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-orange-50/20 to-slate-50 dark:from-slate-950 dark:via-orange-950/10 dark:to-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  const statusData = Object.entries(dashboard.orders_by_status).map(([name, value]) => ({
    name: statusLabels[name] || name,
    value,
    key: name,
  }));

  return (
    <div className="px-3 py-4 space-y-4 sm:px-6 sm:py-6 sm:space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <BarChart3 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Tableau de Bord</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Vue d'ensemble des performances</p>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Total Commandes */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 bg-orange-50 dark:bg-orange-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Commandes</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-slate-100">{dashboard.total_orders}</p>
              </div>
            </div>
          </div>

          {/* Chiffre d'affaires */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '50ms' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Euro className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Chiffre d'affaires</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-slate-100 truncate">
                  {formatAmountHT(dashboard.total_spent)} €
                </p>
              </div>
            </div>
          </div>

          {/* En Attente */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '100ms' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">En Attente</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-slate-100">{dashboard.pending_orders}</p>
              </div>
            </div>
          </div>

          {/* Ce Mois */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300 animate-in fade-in slide-in-from-bottom-4" style={{ animationDelay: '150ms' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Ce Mois</p>
                <p className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-slate-100">{dashboard.orders_this_month}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Graphiques */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4">Commandes par Statut</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.name}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.key] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 24px 24px -1px rgb(0 0 0 / 0.1)',
                    padding: '8px 12px'
                  }}
                  itemStyle={{ color: '#1e293b', fontWeight: 500 }}
                  labelStyle={{ color: '#64748b', marginBottom: '4px' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-green-600 dark:text-green-500" />
              Commandes Récentes
            </h3>
            <div className="space-y-3">
              {dashboard.recent_orders.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                  Aucune commande récente
                </p>
              ) : (
                dashboard.recent_orders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900 dark:text-slate-100">{order.order_number}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(order.order_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-orange-600">{formatAmountHT(order.subtotal ?? order.total_amount)} €</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${order.status === 'delivered' ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' :
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300'
                        }`}>
                        {statusLabels[order.status] || order.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerDashboardTailwind;
